import { useEffect, useState } from "react";
import axios from "axios";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import ApiConfig from "../utils/ApiConfig";
import ApiResponse from "../model/response/ApiResponse";

// Modern Multi-Color Vibrant Palette
const COLORS = {
  registration: "#2563EB",
  paidUsers: "#059669",
  interest: "#D97706",
  dark: "#0F172A",
  lightBg: "#afd1f3",
  cardBg: "#FFFFFF",
  muted: "#64748B",
  pieColors: ["#EC4899", "#3B82F6", "#8B5CF6"],
};

function ReportChart() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  const rawVal = localStorage.getItem("loginVal");
  const loginVal = rawVal ? JSON.parse(rawVal) : null;
  const token = loginVal ? loginVal.token : null;

  // =========================================================
  // API CALL
  // =========================================================
  const getReportData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        ApiConfig.getUrl("User/DashBoardAllCount"),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const api = ApiResponse.fromJson(response.data);

      if (api.statusCode === 200 && api.status === true) {
        setReportData(api.response);
      } else {
        setReportData(null);
      }
    } catch (error) {
      console.error("Report Chart API Error:", error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReportData();
  }, []);

  // =========================================================
  // LOADING
  // =========================================================
  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: COLORS.registration,
          fontWeight: "600",
        }}
      >
        Loading Report Chart...
      </div>
    );
  }

  // =========================================================
  // NO DATA
  // =========================================================
  if (!reportData) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: COLORS.muted,
        }}
      >
        No report data found
      </div>
    );
  }

  // =========================================================
  // DATA PARSING
  // =========================================================

  const monthlyRegistration = (
    reportData.monthlyRegistration || []
  ).map((item) => ({
    ...item,
    totalRegistration: Number(
      item.totalRegistration ||
        item.registration ||
        item.count ||
        0
    ),
  }));

  const monthlyPaidUsers = (
    reportData.monthlyPaidUsers || []
  ).map((item) => ({
    ...item,
    totalPaid: Number(
      item.totalPaid ||
        item.totalPaidUsers ||
        item.paidCount ||
        item.count ||
        0
    ),
  }));

  const genderCount = (reportData.genderCount || []).map(
    (item) => ({
      ...item,
      totalGender: Number(
        item.totalGender || item.count || 0
      ),
    })
  );

  const monthlyInterest = (
    reportData.monthlyInterest || []
  ).map((item) => ({
    ...item,
    totalInterestSend: Number(
      item.totalInterestSend ||
        item.interestSent ||
        item.count ||
        0
    ),
  }));

  // =========================================================
  // CARD TOTALS
  // =========================================================

  const totalRegistrationCount =
    monthlyRegistration.reduce(
      (total, item) =>
        total + item.totalRegistration,
      0
    );

  const totalPaidCount =
    monthlyPaidUsers.reduce(
      (total, item) =>
        total + item.totalPaid,
      0
    );

  const totalInterestCount =
    monthlyInterest.reduce(
      (total, item) =>
        total + item.totalInterestSend,
      0
    );

  const totalGenderCount =
    genderCount.reduce(
      (total, item) =>
        total + item.totalGender,
      0
    );

  // =========================================================
  // REGISTRATION PERCENTAGE
  // =========================================================

  const registrationChartData =
    monthlyRegistration.map((item) => ({
      ...item,
      percentage:
        totalRegistrationCount > 0
          ? (item.totalRegistration /
              totalRegistrationCount) *
            100
          : 0,
    }));

  // =========================================================
  // PAID USERS PERCENTAGE
  // =========================================================

  const paidChartData =
    monthlyPaidUsers.map((item) => ({
      ...item,
      percentage:
        totalPaidCount > 0
          ? (item.totalPaid / totalPaidCount) *
            100
          : 0,
    }));

  // =========================================================
  // INTEREST PERCENTAGE
  // =========================================================

  const interestChartData =
    monthlyInterest.map((item) => ({
      ...item,
      percentage:
        totalInterestCount > 0
          ? (item.totalInterestSend /
              totalInterestCount) *
            100
          : 0,
    }));

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <div
      style={{
        width: "100%",
        padding: "50px",
        backgroundColor: COLORS.lightBg,
        borderRadius: "12px",
        fontFamily:
          "'Inter', system-ui, sans-serif",
      }}
    >
      {/* =====================================================
          TITLE
      ===================================================== */}

      <h3
        style={{
          marginBottom: "20px",
          color: COLORS.dark,
          fontWeight: "700",
        }}
      >
        Report Dashboard
      </h3>

      {/* =====================================================
          TOP COMPACT CARDS
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {/* Registration Card */}

        <div
          style={{
            padding: "12px 16px",
            backgroundColor: COLORS.cardBg,
            borderRadius: "8px",
            boxShadow:
              "0 2px 8px rgba(0,0,0,0.04)",
            borderLeft: `4px solid ${COLORS.registration}`,
          }}
        >
          <div
            style={{
              fontSize: "16px",
              color: COLORS.muted,
              fontWeight: "700",
            }}
          >
            Monthly Registration
          </div>

          <h3
            style={{
              margin: "4px 0 0",
              color: COLORS.dark,
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            {totalRegistrationCount}
          </h3>
        </div>

        {/* Paid Users Card */}

        <div
          style={{
            padding: "12px 16px",
            backgroundColor: COLORS.cardBg,
            borderRadius: "8px",
            boxShadow:
              "0 2px 8px rgba(0,0,0,0.04)",
            borderLeft: `4px solid ${COLORS.paidUsers}`,
          }}
        >
          <div
            style={{
              fontSize: "16px",
              color: COLORS.muted,
              fontWeight: "700",
            }}
          >
            Paid Users
          </div>

          <h3
            style={{
              margin: "4px 0 0",
              color: COLORS.dark,
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            {totalPaidCount}
          </h3>
        </div>

        {/* Interest Card */}

        <div
          style={{
            padding: "12px 16px",
            backgroundColor: COLORS.cardBg,
            borderRadius: "8px",
            boxShadow:
              "0 2px 8px rgba(0,0,0,0.04)",
            borderLeft: `4px solid ${COLORS.interest}`,
          }}
        >
          <div
            style={{
              fontSize: "16px",
              color: COLORS.muted,
              fontWeight: "700",
            }}
          >
            Interest Sent
          </div>

          <h3
            style={{
              margin: "4px 0 0",
              color: COLORS.dark,
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            {totalInterestCount}
          </h3>
        </div>
      </div>

      {/* =====================================================
          CHART ROW 1
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        {/* ===================================================
            MONTHLY REGISTRATION
        =================================================== */}

        <div
          style={{
            backgroundColor: COLORS.cardBg,
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <h6
            style={{
              marginBottom: "12px",
              color: COLORS.dark,
              fontSize: "15px",
              fontWeight: "700",
            }}
          >
            Monthly Registration
          </h6>

          <ResponsiveContainer
            width="100%"
            height={230}
          >
            <BarChart
              data={registrationChartData}
              margin={{
                top: 20,
                right: 10,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#F1F5F9"
              />

              <XAxis
                dataKey="monthName"
                tick={{ fontSize: 11 }}
              />

              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />

              <Tooltip
                formatter={(value, name, props) => [
                  `${value} (${Number(
                    props.payload.percentage
                  ).toFixed(1)}%)`,
                  name,
                ]}
              />

              <Bar
                dataKey="totalRegistration"
                name="Registration"
                fill={COLORS.registration}
                radius={[4, 4, 0, 0]}
                barSize={24}
              >
                <LabelList
                  dataKey="percentage"
                  position="top"
                  formatter={(value) =>
                    `${Number(value).toFixed(0)}%`
                  }
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ===================================================
            MALE & FEMALE
        =================================================== */}

        <div
          style={{
            backgroundColor: COLORS.cardBg,
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <h6
            style={{
              marginBottom: "10px",
              color: COLORS.dark,
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            Male & Female Users
          </h6>

          <ResponsiveContainer
            width="100%"
            height={300}
          >
            <PieChart>
              <Pie
                data={genderCount}
                dataKey="totalGender"
                nameKey="gender"
                cx="50%"
                cy="45%"
                outerRadius={75}
                paddingAngle={2}
                label={({
                  gender,
                  totalGender,
                }) => {
                  const percentage =
                    totalGenderCount > 0
                      ? (Number(totalGender) /
                          totalGenderCount) *
                        100
                      : 0;

                  return `${gender}: ${percentage.toFixed(
                    0
                  )}%`;
                }}
                labelLine={true}
              >
                {genderCount.map(
                  (entry, index) => (
                    <Cell
                      key={`gender-${index}`}
                      fill={
                        COLORS.pieColors[
                          index %
                            COLORS.pieColors
                              .length
                        ]
                      }
                    />
                  )
                )}
              </Pie>

              <Tooltip
                formatter={(
                  value,
                  name
                ) => {
                  const percentage =
                    totalGenderCount > 0
                      ? (Number(value) /
                          totalGenderCount) *
                        100
                      : 0;

                  return [
                    `${value} (${percentage.toFixed(
                      1
                    )}%)`,
                    name,
                  ];
                }}
              />

              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{
                  fontSize: "11px",
                  paddingTop: "5px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* =====================================================
          CHART ROW 2
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
        }}
      >
        {/* ===================================================
            MONTHLY INTEREST
        =================================================== */}

        <div
          style={{
            backgroundColor: COLORS.cardBg,
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <h6
            style={{
              marginBottom: "12px",
              color: COLORS.dark,
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            Monthly Interest Sent
          </h6>

          <ResponsiveContainer
            width="100%"
            height={220}
          >
            <LineChart
              data={interestChartData}
              margin={{
                top: 20,
                right: 10,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#F1F5F9"
              />

              <XAxis
                dataKey="monthName"
                tick={{ fontSize: 11 }}
              />

              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />

              <Tooltip
                formatter={(
                  value,
                  name,
                  props
                ) => [
                  `${value} (${Number(
                    props.payload.percentage
                  ).toFixed(1)}%)`,
                  name,
                ]}
              />

              <Line
                type="monotone"
                dataKey="totalInterestSend"
                name="Interest Sent"
                stroke={COLORS.interest}
                strokeWidth={2.5}
                dot={{ r: 4 }}
              >
                <LabelList
                  dataKey="percentage"
                  position="top"
                  formatter={(value) =>
                    `${Number(value).toFixed(0)}%`
                  }
                />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ===================================================
            MONTHLY PAID USERS
        =================================================== */}

        <div
          style={{
            backgroundColor: COLORS.cardBg,
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <h6
            style={{
              marginBottom: "12px",
              color: COLORS.dark,
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            Monthly Paid Users
          </h6>

          <ResponsiveContainer
            width="100%"
            height={230}
          >
            <BarChart
              data={paidChartData}
              margin={{
                top: 20,
                right: 10,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#F1F5F9"
              />

              <XAxis
                dataKey="monthName"
                tick={{ fontSize: 11 }}
              />

              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />

              <Tooltip
                formatter={(
                  value,
                  name,
                  props
                ) => [
                  `${value} (${Number(
                    props.payload.percentage
                  ).toFixed(1)}%)`,
                  name,
                ]}
              />

              <Bar
                dataKey="totalPaid"
                name="Paid Users"
                fill={COLORS.paidUsers}
                radius={[4, 4, 0, 0]}
                barSize={24}
              >
                <LabelList
                  dataKey="percentage"
                  position="top"
                  formatter={(value) =>
                    `${Number(value).toFixed(0)}%`
                  }
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default ReportChart;